// Superadmin-only platform defaults (feature `deepinfra-qwen-provider`, R8).
// Deployment-wide toggles + default provider templates. Changes apply to
// organizations created AFTER the save — never retroactively.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  getPlatformSettings,
  listProviderTemplates,
  type PlatformSettings,
  updatePlatformSettings,
} from '../../api/providers'
import { listEmbeddingModels, listRerankModels } from '../../api/settings'
import ModelPicker from '../../components/ModelPicker'
import { recommendedRemoteDimensions } from '../../embedding/capabilities'
import type { EmbeddingProvider, ProviderTemplate } from '../../types/api'

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

const EMBEDDING_TEMPLATE_PROVIDERS: Array<{ value: EmbeddingProvider; label: string }> = [
  { value: 'openai_compatible', label: 'OpenAI-compatible (incl. DeepInfra embeddings)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'nvidia_nim', label: 'NVIDIA NIM' },
  { value: 'lm_studio', label: 'LM Studio' },
]

const RERANK_TEMPLATE_PROVIDERS = [
  { value: 'openai_compatible', label: 'OpenAI-compatible (/rerank endpoint)' },
  { value: 'openrouter', label: 'OpenRouter (chat-model scoring)' },
  { value: 'deepinfra', label: 'DeepInfra (native inference API)' },
]

function isDeepInfraRerankUrl(url: string): boolean {
  return /deepinfra\.com/i.test(url) || /\{model(?:_name)?\}/i.test(url)
}

function detectEmbeddingProvider(url: string): EmbeddingProvider {
  if (/openrouter\.ai/i.test(url)) return 'openrouter'
  if (/api\.openai\.com/i.test(url)) return 'openai'
  if (/integrate\.api\.nvidia\.com/i.test(url)) return 'nvidia_nim'
  if (/localhost:1234/i.test(url)) return 'lm_studio'
  return 'openai_compatible'
}

function detectRerankProvider(url: string): string {
  if (isDeepInfraRerankUrl(url)) return 'deepinfra'
  if (/openrouter\.ai/i.test(url)) return 'openrouter'
  return 'openai_compatible'
}

export default function PlatformSettingsPage() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [templates, setTemplates] = useState<ProviderTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')

  const [localEmbeddingEnabled, setLocalEmbeddingEnabled] = useState(true)
  const [localRerankEnabled, setLocalRerankEnabled] = useState(true)

  const [embProvider, setEmbProvider] = useState('')
  const [embBaseUrl, setEmbBaseUrl] = useState('')
  const [embKey, setEmbKey] = useState('')
  const [embModel, setEmbModel] = useState('')
  const [embDimensions, setEmbDimensions] = useState('')
  const [embProfile, setEmbProfile] = useState('nomic')

  const [rerankProvider, setRerankProvider] = useState('')
  const [rerankBaseUrl, setRerankBaseUrl] = useState('')
  const [rerankKey, setRerankKey] = useState('')
  const [rerankModel, setRerankModel] = useState('')

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const [s, t] = await Promise.all([getPlatformSettings(), listProviderTemplates()])
      setSettings(s)
      setTemplates(t)
      setLocalEmbeddingEnabled(s.localEmbeddingEnabled)
      setLocalRerankEnabled(s.localRerankEnabled)
      setEmbProvider(s.defaultEmbeddingProvider ?? '')
      setEmbBaseUrl(s.defaultEmbeddingApiBaseUrl ?? '')
      setEmbModel(s.defaultEmbeddingModelName ?? '')
      setEmbDimensions(
        s.defaultEmbeddingDimensions !== null && s.defaultEmbeddingDimensions !== undefined
          ? String(s.defaultEmbeddingDimensions)
          : ''
      )
      setEmbProfile(s.defaultEmbeddingPromptProfile ?? 'nomic')
      setRerankProvider(s.defaultRerankProvider ?? '')
      setRerankBaseUrl(s.defaultRerankApiBaseUrl ?? '')
      setRerankModel(s.defaultRerankModelName ?? '')
      setEmbKey('')
      setRerankKey('')
      setSelectedTemplateId('')
    } catch {
      toast.error('Failed to load platform settings')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const applyProviderTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId)
    if (!templateId) return
    const t = templates.find((row) => String(row.id) === templateId)
    if (!t) return

    const embeddingUrl = t.embeddingUrl || t.baseUrl
    const rerankUrl = t.rerankUrl || t.baseUrl
    const embeddingModel = t.embeddingModel?.trim() || ''
    const rerankModelName = t.rerankModel?.trim() || ''

    setEmbProvider(detectEmbeddingProvider(embeddingUrl))
    setEmbBaseUrl(embeddingUrl)
    if (embeddingModel) {
      setEmbModel(embeddingModel)
      setEmbDimensions(String(recommendedRemoteDimensions(embeddingModel)))
      setEmbProfile(/qwen/i.test(embeddingModel) ? 'instruct' : 'nomic')
    }

    setRerankProvider(detectRerankProvider(rerankUrl))
    setRerankBaseUrl(rerankUrl)
    if (rerankModelName) setRerankModel(rerankModelName)

    toast.success(`Filled embedding + rerank fields from “${t.name}”. Add API keys, then save.`)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const dims = embDimensions.trim()
      const updated = await updatePlatformSettings({
        localEmbeddingEnabled,
        localRerankEnabled,
        defaultEmbeddingProvider: embProvider || null,
        defaultEmbeddingApiBaseUrl: embBaseUrl.trim() || null,
        ...(embKey.trim() ? { defaultEmbeddingApiKey: embKey.trim() } : {}),
        defaultEmbeddingModelName: embModel.trim() || null,
        defaultEmbeddingDimensions: dims ? Number.parseInt(dims, 10) : null,
        defaultEmbeddingPromptProfile: embProfile || null,
        defaultRerankProvider: rerankProvider || null,
        defaultRerankApiBaseUrl: rerankBaseUrl.trim() || null,
        ...(rerankKey.trim() ? { defaultRerankApiKey: rerankKey.trim() } : {}),
        defaultRerankModelName: rerankModel.trim() || null,
      })
      setSettings(updated)
      setEmbKey('')
      setRerankKey('')
      toast.success('Platform settings saved — applies to new organizations only')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save platform settings')
    } finally {
      setIsSaving(false)
    }
  }

  const clearEmbeddingTemplate = () => {
    setEmbProvider('')
    setEmbBaseUrl('')
    setEmbKey('')
    setEmbModel('')
    setEmbDimensions('')
    setEmbProfile('nomic')
  }

  const clearRerankTemplate = () => {
    setRerankProvider('')
    setRerankBaseUrl('')
    setRerankKey('')
    setRerankModel('')
  }

  if (isLoading) {
    return <p className="text-sm text-harbor-text-muted">Loading platform settings…</p>
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-xl font-semibold text-harbor-text">Platform Settings</h1>
        <p className="text-sm text-harbor-text-muted mt-1">
          Deployment-wide defaults for new organizations. Editing the template later does not change
          any already-created organization&apos;s providers or any existing project&apos;s embedding
          identity — it applies to new organizations only.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <section className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-harbor-text">Local models</h2>
          <label className="flex items-start gap-3 text-sm text-harbor-text">
            <input
              type="checkbox"
              checked={localEmbeddingEnabled}
              onChange={(e) => setLocalEmbeddingEnabled(e.target.checked)}
              className="mt-0.5 accent-emerald-500"
            />
            <span>
              Local embedding supported
              <span className="block text-xs text-harbor-text-muted">
                When off, new provider pickers hide the local option and the server skips eager ONNX
                construction at boot (existing local rows keep working). Pair with a default
                embedding template below so new orgs are seeded with a remote provider instead of
                falling back to Nomic.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm text-harbor-text">
            <input
              type="checkbox"
              checked={localRerankEnabled}
              onChange={(e) => setLocalRerankEnabled(e.target.checked)}
              className="mt-0.5 accent-emerald-500"
            />
            <span>
              Local rerank supported
              <span className="block text-xs text-harbor-text-muted">
                When off, new provider pickers hide the local reranker option.
              </span>
            </span>
          </label>
        </section>

        <section className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-3">
          <h2 className="text-lg font-semibold text-harbor-text">Fill from provider template</h2>
          <p className="text-xs text-harbor-text-muted">
            Provider Templates define per-service URLs (including DeepInfra&apos;s native{' '}
            <code className="text-[11px]">/inference/{'{model}'}</code> rerank path). Pick one to
            copy embedding + rerank addresses and suggested models into the fields below. DeepInfra
            embeddings use the OpenAI-compatible provider type — there is no separate{' '}
            <code className="text-[11px]">deepinfra</code> embedding enum.
          </p>
          <select
            className={inputClassName}
            value={selectedTemplateId}
            onChange={(e) => applyProviderTemplate(e.target.value)}
          >
            <option value="">(none)</option>
            {templates.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </select>
          {templates.length === 0 && (
            <p className="text-xs text-amber-400">
              No active provider templates yet. Create one under Provider Templates, then return
              here.
            </p>
          )}
        </section>

        <section className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-harbor-text">Default embedding provider</h2>
            <button
              type="button"
              onClick={clearEmbeddingTemplate}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
            >
              Clear template
            </button>
          </div>
          <p className="text-xs text-harbor-text-muted">
            No template = new organizations fall back to local, exactly as today.
            {settings?.defaultEmbeddingApiKeySet && (
              <span className="block mt-1">
                A key is stored (masked). Leave the key field blank to keep it.
              </span>
            )}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">Provider</label>
              <select
                className={inputClassName}
                value={embProvider}
                onChange={(e) => setEmbProvider(e.target.value)}
              >
                <option value="">(none — local default)</option>
                {EMBEDDING_TEMPLATE_PROVIDERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">
                Prompt profile
              </label>
              <select
                className={inputClassName}
                value={embProfile}
                onChange={(e) => setEmbProfile(e.target.value)}
              >
                <option value="nomic">Nomic</option>
                <option value="instruct">Instruct (Qwen3-style)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">API base URL</label>
              <input
                className={inputClassName}
                value={embBaseUrl}
                onChange={(e) => setEmbBaseUrl(e.target.value)}
                placeholder="https://api.deepinfra.com/v1/openai"
              />
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">API key</label>
              <input
                type="password"
                className={inputClassName}
                value={embKey}
                onChange={(e) => setEmbKey(e.target.value)}
                placeholder={
                  settings?.defaultEmbeddingApiKeySet ? 'Leave blank to keep current key' : ''
                }
                autoComplete="off"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">Model</label>
              <ModelPicker
                apiBaseUrl={embBaseUrl}
                apiKey={embKey}
                authHeader="authorization_bearer"
                value={embModel}
                onChange={(model) => {
                  setEmbModel(model)
                  if (model.trim() && !embDimensions.trim()) {
                    setEmbDimensions(String(recommendedRemoteDimensions(model)))
                  }
                  if (/qwen/i.test(model)) setEmbProfile('instruct')
                }}
                inputClassName={inputClassName}
                id="platform-emb-model"
                kind="embedding"
                loadModels={embBaseUrl.trim() ? listEmbeddingModels : undefined}
              />
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">Dimensions</label>
              <input
                className={inputClassName}
                value={embDimensions}
                onChange={(e) => setEmbDimensions(e.target.value)}
                placeholder="1024"
                inputMode="numeric"
              />
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-harbor-text">Default rerank provider</h2>
            <button
              type="button"
              onClick={clearRerankTemplate}
              className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
            >
              Clear template
            </button>
          </div>
          <p className="text-xs text-harbor-text-muted">
            No template = new organizations fall back to local, exactly as today. For DeepInfra, use
            the native inference URL from your provider template (often{' '}
            <code className="text-[11px]">https://api.deepinfra.com/v1/inference/{'{model}'}</code>
            ), not the OpenAI-compatible base.
            {settings?.defaultRerankApiKeySet && (
              <span className="block mt-1">
                A key is stored (masked). Leave the key field blank to keep it.
              </span>
            )}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">Provider</label>
              <select
                className={inputClassName}
                value={rerankProvider}
                onChange={(e) => {
                  const next = e.target.value
                  setRerankProvider(next)
                  if (next === 'deepinfra' && !rerankBaseUrl.trim()) {
                    const fromTemplate = templates.find((t) => isDeepInfraRerankUrl(t.rerankUrl))
                    if (fromTemplate) {
                      setRerankBaseUrl(fromTemplate.rerankUrl)
                      if (fromTemplate.rerankModel) setRerankModel(fromTemplate.rerankModel)
                    } else {
                      setRerankBaseUrl('https://api.deepinfra.com/v1/inference/{model}')
                    }
                  }
                }}
              >
                <option value="">(none — local default)</option>
                {RERANK_TEMPLATE_PROVIDERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">Model</label>
              <ModelPicker
                apiBaseUrl={rerankBaseUrl}
                apiKey={rerankKey}
                authHeader="authorization_bearer"
                value={rerankModel}
                onChange={setRerankModel}
                inputClassName={inputClassName}
                id="platform-rerank-model"
                kind="rerank"
                loadModels={
                  rerankBaseUrl.trim() && rerankProvider !== 'deepinfra'
                    ? listRerankModels
                    : undefined
                }
              />
              {rerankProvider === 'deepinfra' && (
                <p className="text-xs text-harbor-text-muted mt-1">
                  No discovery for DeepInfra rerankers — enter the fixed model name (e.g.
                  Qwen/Qwen3-Reranker-4B).
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">API base URL</label>
              <input
                className={inputClassName}
                value={rerankBaseUrl}
                onChange={(e) => setRerankBaseUrl(e.target.value)}
                placeholder="https://api.deepinfra.com/v1/inference/{model}"
              />
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1">API key</label>
              <input
                type="password"
                className={inputClassName}
                value={rerankKey}
                onChange={(e) => setRerankKey(e.target.value)}
                placeholder={
                  settings?.defaultRerankApiKeySet ? 'Leave blank to keep current key' : ''
                }
                autoComplete="off"
              />
            </div>
          </div>
        </section>

        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save platform settings'}
        </button>
      </form>
    </div>
  )
}
